import { useState, useEffect, useRef, forwardRef, useCallback, useMemo } from 'react';
import { useGroupKey, useInlineFieldEditingHoverStyles, useJoinKeyPathToString, useRegisterInlineFieldEditor, useWasUndoRedoEventWithData } from '../lib/InlineFieldEditingHooks';
import styled from 'styled-components';
// @ts-expect-error not typed
import { useBus } from '../context/Bus';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { View } from 'foundations-components/transitional/components/View';
import { computeUpdatedImageSizePreservingAspectRatio, updateImageDom } from '../lib/domUtils/imageDomUtils';
import ImageSizeTypes from 'ContentUtils/constants/ImageSizeTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
// @ts-expect-error not typed
import { makeAsyncComponent } from 'ContentEditorUI/components/shared/makeAsyncUtils';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { ReplaceIcon } from 'foundations-assets/icons/components/ReplaceIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CORNER_WIDTH = 9;
const AsyncImageFieldPopupEditor = makeAsyncComponent(() => import( /* webpackChunkName: 'ImageFieldPopupEditor' */'InpageEditorUI/components/ImageFieldPopupEditor'));
const DRAGGABLE_RESIZE_CORNER_TO_CURSOR = {
  topleft: 'nwse-resize',
  topright: 'nesw-resize',
  bottomleft: 'nesw-resize',
  bottomright: 'nwse-resize'
};
const toPx = px => `${px}px`;
function offsetPositionToDocument(positionType, positionPx, offsetParentRect, docScrollTop) {
  const bodyToPageOffset = offsetParentRect.top + docScrollTop;

  // Translating the rect by `bodyToPageOffset` in the y-direction means adding to both `top` and `bottom`
  switch (positionType) {
    case 'top':
      {
        return positionPx - offsetParentRect.top + bodyToPageOffset;
      }
    case 'right':
      {
        return positionPx - offsetParentRect.left;
      }
    case 'left':
      {
        return positionPx - offsetParentRect.left;
      }
    case 'bottom':
      {
        return positionPx - offsetParentRect.top + bodyToPageOffset;
      }
    default:
      throw new Error('Unexpected position type');
  }
}
function useCursorTypeForCorner(cornerType) {
  const cursor = DRAGGABLE_RESIZE_CORNER_TO_CURSOR[cornerType];
  if (!cursor) {
    console.error(`No cursor type for ${cornerType}`);
    return 'auto';
  }
  return cursor;
}
const StyledCornerElement = styled.div.withConfig({
  displayName: "ImageFieldEditor__StyledCornerElement",
  componentId: "sc-1ijcv6m-0"
})(({
  theme,
  $cursor,
  $top,
  $left
}) => {
  return {
    width: CORNER_WIDTH,
    height: CORNER_WIDTH,
    position: 'absolute',
    top: $top,
    left: $left,
    backgroundColor: theme.color['fill-info-default'],
    zIndex: 2147483598,
    cursor: $cursor
  };
});
const ImageFieldEditorCorner = /*#__PURE__*/forwardRef(({
  point,
  cornerType,
  onMouseDown
}, ref) => {
  const [x, y] = point;
  const cursor = useCursorTypeForCorner(cornerType);
  const top = Math.round(y - CORNER_WIDTH / 2);
  const left = Math.round(x - CORNER_WIDTH / 2);
  return /*#__PURE__*/_jsx(StyledCornerElement, {
    ref: ref,
    $cursor: cursor,
    $top: top,
    $left: left,
    onMouseDown: e => {
      e.stopPropagation();
      onMouseDown(e, cornerType);
    }
  });
});
const offsetCornerPositionToCornerWidth = position => Math.round(position - CORNER_WIDTH / 2);
function updateRefPositions(rect, corner, element) {
  let left;
  let top;
  const offsetParent = document.body;
  const offsetParentRect = offsetParent.getBoundingClientRect();
  const docScrollTop = document.documentElement.scrollTop;
  switch (corner) {
    case 'topright':
      {
        left = offsetPositionToDocument('right', offsetCornerPositionToCornerWidth(rect.right), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('top', offsetCornerPositionToCornerWidth(rect.top), offsetParentRect, docScrollTop);
        break;
      }
    case 'topleft':
      {
        left = offsetPositionToDocument('left', offsetCornerPositionToCornerWidth(rect.left), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('top', offsetCornerPositionToCornerWidth(rect.top), offsetParentRect, docScrollTop);
        break;
      }
    case 'bottomleft':
      {
        left = offsetPositionToDocument('left', offsetCornerPositionToCornerWidth(rect.left), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('bottom', offsetCornerPositionToCornerWidth(rect.bottom), offsetParentRect, docScrollTop);
        break;
      }
    case 'bottomright':
      {
        left = offsetPositionToDocument('right', offsetCornerPositionToCornerWidth(rect.right), offsetParentRect, docScrollTop);
        top = offsetPositionToDocument('bottom', offsetCornerPositionToCornerWidth(rect.bottom), offsetParentRect, docScrollTop);
        break;
      }
    default:
      return;
  }
  element.style.top = toPx(top);
  element.style.left = toPx(left);
}
function ImageEditorTooltipContent({
  contentRef,
  node,
  aspectRatio,
  resizable,
  responsive,
  mergeUpdatedSubFieldValues,
  currentValue,
  showLoading
}) {
  const bus = useBus();
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const handleReplaceImageClick = useCallback(() => {
    bus.postMessage({
      action: 'inpageOpenFilePicker'
    });
    setFilePickerOpen(true);
  }, [bus]);
  const handleFilePickerResponse = useCallback(url => {
    const newImage = new Image();
    newImage.src = url;
    newImage.decode().then(() => {
      const updatedValueToMerge = {
        src: url
      };
      const sizeType = currentValue.size_type;
      if (!sizeType || sizeType === ImageSizeTypes.AUTO) {
        mergeUpdatedSubFieldValues(updatedValueToMerge, true);
        return;
      }
      if (sizeType === ImageSizeTypes.AUTO_CUSTOM_MAX) {
        updatedValueToMerge.max_width = newImage.width;
        updatedValueToMerge.max_height = newImage.height;
      } else {
        updatedValueToMerge.width = newImage.width;
        updatedValueToMerge.height = newImage.height;
      }
      mergeUpdatedSubFieldValues(updatedValueToMerge, true);
    }).catch(() => {
      console.error('image failed to load');
      mergeUpdatedSubFieldValues({
        src: url
      }, true);
    });
    // TODO: HubDB stores the img src on `url`?
  }, [mergeUpdatedSubFieldValues, currentValue.size_type]);
  const handleLazyLoadingChange = useCallback(loadingType => {
    // We currently don't "live-update" loading states since it won't really have an affect
    mergeUpdatedSubFieldValues({
      loading: loadingType
    });
  }, [mergeUpdatedSubFieldValues]);
  const [editingOpen, setEditingOpen] = useState(false);
  useEffect(() => {
    if (filePickerOpen) {
      const handleImageChosen = ({
        data
      }) => {
        handleFilePickerResponse(data.url);
        setFilePickerOpen(false);
      };
      const handleFilePickerClosed = () => {
        setFilePickerOpen(false);
      };
      // Have to bind event listeners conditonally because `ImageFieldPopupEditor` is also listening to the same action
      bus.on('post:filePickerImageChosen', handleImageChosen);
      bus.on('post:inpageFilePickerClosed', handleFilePickerClosed);
      return () => {
        bus.off('post:filePickerImageChosen', handleImageChosen);
        bus.off('post:inpageFilePickerClosed', handleFilePickerClosed);
      };
    } else {
      return () => {};
    }
  }, [bus, filePickerOpen, handleFilePickerResponse]);
  if (editingOpen) {
    return /*#__PURE__*/_jsxs(View, {
      ref: contentRef,
      paddingInline: "400",
      paddingBlock: "250",
      children: [/*#__PURE__*/_jsx(AsyncImageFieldPopupEditor, {
        node: node,
        aspectRatio: aspectRatio,
        resizable: resizable,
        responsive: responsive,
        mergeUpdatedSubFieldValues: mergeUpdatedSubFieldValues,
        currentValue: currentValue,
        onReplace: handleFilePickerResponse,
        onSizeChange: sizeUpdate => {
          // This case is a bit more complex so we defer the DOM update to our util
          mergeUpdatedSubFieldValues(sizeUpdate, true);
        },
        onAltChange: e => {
          /*  @ts-expect-error Aug-22-2024, 14:7UTC React types upgrade forwards compatibility  */
          const updatedAlt = e.target.value;
          node.setAttribute('alt', updatedAlt);
          mergeUpdatedSubFieldValues({
            alt: updatedAlt
          });
        },
        showLoading: showLoading,
        onLazyLoadingChange: handleLazyLoadingChange
      }), /*#__PURE__*/_jsx(Button, {
        onClick: e => {
          e.stopPropagation();
          setEditingOpen(false);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "inpage.inlineFieldEditors.closeButton"
        })
      })]
    });
  }
  return /*#__PURE__*/_jsx("div", {
    ref: contentRef,
    children: /*#__PURE__*/_jsxs(ButtonGroup, {
      paddingInline: "400",
      paddingBlock: "250",
      children: [/*#__PURE__*/_jsx(IconButton, {
        size: "small",
        use: "tertiary-light",
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "inpage.inlineFieldEditors.editImage"
        }),
        onClick: e => {
          e.stopPropagation();
          setEditingOpen(true);
        },
        children: /*#__PURE__*/_jsx(EditIcon, {})
      }), /*#__PURE__*/_jsx(IconButton, {
        size: "small",
        use: "tertiary-light",
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "inpage.inlineFieldEditors.replaceImage"
        }),
        onClick: handleReplaceImageClick,
        children: /*#__PURE__*/_jsx(ReplaceIcon, {})
      })]
    })
  });
}
const StyledImageFieldEditorContainer = styled.div.withConfig({
  displayName: "ImageFieldEditor__StyledImageFieldEditorContainer",
  componentId: "sc-1ijcv6m-1"
})([".hs-private.hs-private.hs-private &{position:absolute;top:0;left:0;z-index:2147483598;}"]);
export default function ImageFieldEditor({
  node,
  schema,
  onUpdate,
  currentValue,
  name,
  keyPath,
  moduleName
}) {
  useInlineFieldEditingHoverStyles(node);
  const wasUndoRedo = useWasUndoRedoEventWithData(currentValue);
  const [isActive, setIsActive] = useState(false);
  const {
    resizable,
    responsive,
    show_loading: showLoading
  } = schema;
  useRegisterInlineFieldEditor(moduleName);
  const fieldKeyString = useJoinKeyPathToString(keyPath);
  const groupKey = useGroupKey(moduleName, fieldKeyString, name);

  // Can become problematic from a perf perspective
  const mergeUpdatedSubFieldValues = useCallback((updatedValues, applyChangesToDom = false) => {
    const mergedValue = Object.assign({}, currentValue, updatedValues);
    if (applyChangesToDom) {
      updateImageDom(node, mergedValue);
    }
    onUpdate(keyPath, mergedValue, {
      groupKey
    });
  }, [currentValue, groupKey, keyPath, node, onUpdate]);
  useEffect(() => {
    const onClick = () => {
      setIsActive(true);
    };
    node.addEventListener('click', onClick);
    return () => {
      node.removeEventListener('click', onClick);
    };
  }, [node]);
  const shadowedIsActive = useRef(isActive);
  shadowedIsActive.current = isActive;
  const sizeType = currentValue.size_type;
  const [isDragging, setIsDragging] = useState(false);
  const skipNextClickRef = useRef(false);
  const draggingCorner = useRef(null);
  const originalRectRef = useRef(null);
  const dragSource = useRef(null);
  const imageSizeTypeRef = useRef(sizeType);
  imageSizeTypeRef.current = sizeType;
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const inlineImageFieldEditorRef = useRef(null);
  const nodeSrc = node.getAttribute('src');
  const aspectRatio = useMemo(() => {
    const rect = node.getBoundingClientRect();
    return rect.width / rect.height;
    // Need to ignore the deps rule here because we want to make sure
    // that we recompute the aspect ratio whenever the src of the image
    // changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, nodeSrc]);
  const lastUpdatedWidthAndHeightRef = useRef({
    width: 0,
    height: 0
  });
  const transientlyUpdateRefPositions = useCallback(clientRect => {
    if (bottomLeftRef.current && bottomRightRef.current && topRightRef.current && topLeftRef.current) {
      updateRefPositions(clientRect, 'topright', topRightRef.current);
      updateRefPositions(clientRect, 'bottomright', bottomRightRef.current);
      updateRefPositions(clientRect, 'bottomleft', bottomLeftRef.current);
      updateRefPositions(clientRect, 'topleft', topLeftRef.current);
    }
  }, []);
  useEffect(() => {
    if (
    // Always apply the `currentValue` if the change came from an undo/redo action
    shadowedIsActive.current === false || wasUndoRedo) {
      updateImageDom(node, currentValue);
      if (shadowedIsActive.current === true) {
        const newBoundingRect = node.getBoundingClientRect();
        transientlyUpdateRefPositions(newBoundingRect);
      }
    }
  }, [currentValue, node, transientlyUpdateRefPositions, wasUndoRedo]);
  useEffect(() => {
    const globalClickListener = e => {
      if (skipNextClickRef.current) {
        skipNextClickRef.current = false;
        return;
      }
      if (shadowedIsActive.current && e.target instanceof HTMLElement && inlineImageFieldEditorRef.current) {
        const inlineImageFieldEditor = inlineImageFieldEditorRef.current;
        const moduleOverlayElement = document.querySelector('[data-test-id="layout-section-hover-overlay"]');
        // We first capture the re-fired event from `HoverOverlayContainer`, set ourselves to active
        // and then re-render. Then, the original click event on the module overlay is captured here
        // So, we need to check if the target for the event is the hover overlay
        // and if it is, then if the next deepest element at the point is our image element.
        // In that case, we want to ignore the click so that we don't end up re-setting our
        // active state back to false
        if (moduleOverlayElement && e.target === moduleOverlayElement) {
          moduleOverlayElement.style.display = 'none';
          const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY);
          if (node.isSameNode(elementAtPoint)) {
            moduleOverlayElement.style.display = 'block';
            return;
          }
          moduleOverlayElement.style.display = 'block';
        }
        if (inlineImageFieldEditor && inlineImageFieldEditor.contains(e.target)) {
          // Maybe do a double-click thing here where we open the edit tooltip
          // if someone double clicks on the active image?
          return;
        }
        if (!node.contains(e.target)) {
          setIsActive(false);
        }
      }
    };
    const globalMouseMoveListener = e => {
      if (isDragging && originalRectRef.current) {
        if (!originalRectRef.current || !draggingCorner.current || !dragSource.current) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const originalRect = originalRectRef.current;
        const x = e.clientX;
        const y = e.clientY;
        const updatedImageDimensions = computeUpdatedImageSizePreservingAspectRatio(originalRect, draggingCorner.current, dragSource.current, x, y);
        if (!updatedImageDimensions) {
          return;
        }
        let {
          newWidth,
          newHeight
        } = updatedImageDimensions;
        newWidth = newWidth > 0 ? newWidth : 1;
        newHeight = newHeight > 0 ? newHeight : 1;
        lastUpdatedWidthAndHeightRef.current.width = newWidth;
        lastUpdatedWidthAndHeightRef.current.height = newHeight;
        // This logic will need to be kept up to date with ImageWidgetTag.java within the cos-renderer
        // Also this is shared in a few places, maybe worth moving it at this point
        if (imageSizeTypeRef.current === ImageSizeTypes.AUTO_CUSTOM_MAX) {
          node.setAttribute('width', '100%');
          node.setAttribute('height', 'auto');
          node.style.removeProperty('width');
          node.style.removeProperty('height');
          node.style.maxWidth = toPx(newWidth);
          node.style.maxHeight = toPx(newHeight);
        } else {
          node.setAttribute('width', toPx(newWidth));
          node.setAttribute('height', toPx(newHeight));
          node.style.removeProperty('width');
          node.style.removeProperty('height');
          node.style.removeProperty('max-width');
          node.style.removeProperty('max-height');
        }
        transientlyUpdateRefPositions(node.getBoundingClientRect());
      }
    };
    const globalMouseUpListener = e => {
      if (isDragging) {
        e.stopPropagation();
        skipNextClickRef.current = true;
        setIsDragging(false);
        if (imageSizeTypeRef.current === ImageSizeTypes.AUTO_CUSTOM_MAX) {
          const {
            width,
            height
          } = lastUpdatedWidthAndHeightRef.current;
          mergeUpdatedSubFieldValues({
            max_width: width,
            max_height: height
          });
        } else {
          mergeUpdatedSubFieldValues(lastUpdatedWidthAndHeightRef.current);
        }
        lastUpdatedWidthAndHeightRef.current.width = 0;
        lastUpdatedWidthAndHeightRef.current.height = 0;
      }
    };
    document.addEventListener('click', globalClickListener);
    document.addEventListener('mousemove', globalMouseMoveListener);
    document.addEventListener('mouseup', globalMouseUpListener);
    return () => {
      document.removeEventListener('click', globalClickListener);
      document.removeEventListener('mousemove', globalMouseMoveListener);
      document.removeEventListener('mouseup', globalMouseUpListener);
    };
  }, [node, isDragging, mergeUpdatedSubFieldValues, transientlyUpdateRefPositions]);
  useEffect(() => {
    const onResizeOrScroll = () => {
      transientlyUpdateRefPositions(node.getBoundingClientRect());
    };
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll);
    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll);
    };
  }, [node, transientlyUpdateRefPositions]);
  const onMouseDown = useCallback((e, selectedCorner) => {
    // Potentially risky because we could maybe end up in a case where we never set this to false
    setIsDragging(true);
    draggingCorner.current = selectedCorner;
    originalRectRef.current = node.getBoundingClientRect();
    dragSource.current = {
      x: e.clientX,
      y: e.clientY
    };
  }, [node]);
  if (!isActive) {
    return null;
  }
  const nodeRect = node.getBoundingClientRect();
  const offsetParent = document.body;
  const offsetParentRect = offsetParent.getBoundingClientRect();
  const bodyToPageOffset = offsetParentRect.top + document.documentElement.scrollTop;
  // Translating the rect by `bodyToPageOffset` in the y-direction means adding to both `top` and `bottom`
  const top = nodeRect.top - offsetParentRect.top + bodyToPageOffset;
  const bottom = nodeRect.bottom - offsetParentRect.top + bodyToPageOffset;
  const left = nodeRect.left - offsetParentRect.left;
  const right = nodeRect.right - offsetParentRect.left;
  const points = [[left, top], [right, top], [left, bottom], [right, bottom]];
  const showDraggableCorners = resizable && sizeType !== ImageSizeTypes.AUTO;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized needs to set hs-private for styled-components namespace
    _jsxs(StyledImageFieldEditorContainer, {
      className: "hs-private",
      children: [showDraggableCorners && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "topleft",
          point: points[0],
          onMouseDown: onMouseDown,
          ref: topLeftRef
        }), /*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "topright",
          point: points[1],
          onMouseDown: onMouseDown,
          ref: topRightRef
        }), /*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "bottomleft",
          point: points[2],
          onMouseDown: onMouseDown,
          ref: bottomLeftRef
        }), /*#__PURE__*/_jsx(ImageFieldEditorCorner, {
          cornerType: "bottomright",
          point: points[3],
          onMouseDown: onMouseDown,
          ref: bottomRightRef
        })]
      }), /*#__PURE__*/_jsx(Popover, {
        target: node,
        open: !isDragging,
        placement: "top",
        content: /*#__PURE__*/_jsx(ImageEditorTooltipContent, {
          contentRef: inlineImageFieldEditorRef,
          node: node,
          aspectRatio: aspectRatio,
          resizable: resizable,
          responsive: responsive,
          mergeUpdatedSubFieldValues: mergeUpdatedSubFieldValues,
          currentValue: currentValue,
          showLoading: showLoading
        })
      })]
    })
  );
}